import {
  RXDB_INVOICE_COLLECTION_NAME,
  type Invoice as RxInvoice,
} from "$global-providers/rxdb-provider/collections/invoice";
import type { RxDocument } from "rxdb";
import { useRxData } from "rxdb-hooks";

export interface FetchInvoiceRxdbResult {
  /**
   * Live rxdb document. Call-sites mutate via `doc.patch({...})` directly —
   * there are no per-action wrapper hooks in v2.
   */
  doc: RxDocument<RxInvoice> | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Reads a single invoice from the local rxdb store by primary key
 * (the rxdb `id`, which the api-billing syncable mapper sets to
 * `mongoose.uuid`).
 *
 * Returns the live `RxDocument` so consumers can both read fields off
 * it and mutate via `doc.patch({ ... })` inline — no per-action hook
 * proliferation.
 *
 * Mapping the document into the legacy `InvoiceCardDetailsInfo` shape
 * happens in the v2 sub-section/modal components that need it, where
 * joins with `priceList` / patient rxdb collections happen at the
 * presentation layer.
 */
export function useFetchInvoiceRxdb(
  syncInvoiceId: string,
): FetchInvoiceRxdbResult {
  // `useRxData` with a primary-key selector is the same shape the
  // list query uses (which is known to wake the hook up). Using
  // `findOne(id)` here instead of `find({selector:{id}})` would
  // trigger an rxdb-hooks bug where the observable's initial
  // emission doesn't always reach the hook once the collection is
  // ready.
  const { result, isFetching } = useRxData<RxInvoice>(
    RXDB_INVOICE_COLLECTION_NAME,
    (collection) => collection.find({ selector: { id: syncInvoiceId } }),
  );

  const doc = (result?.[0] as RxDocument<RxInvoice> | undefined) ?? null;

  return { doc, loading: isFetching, error: null };
}
